import DxButton from "devextreme-react/button";
import React from "react";
import { PhaseWorkflowType } from "../../../../../dtos/PhaseWorkflowType";
import StatusIcon from "../../../StatusIcon";
import { WORKFLOW_TYPE_LABELS } from "../../../../tasklist/useWorkflowTypeInfo";
import { TasklistMode } from "../../context/TaskListContext";
import { PhaseNameHeading } from "./PhaseNameHeading";
import "./TaskStatusIconView.css";

type ITaskStatusIconViewProps = {
    isVisible: boolean;
    statusInfo: { text: string; color: string; border?: string } | null;
    handleClick: () => void;
    cursor: string;
    phaseName: string;
    dueDateText: string;
    textOnly?: boolean;
    workflowType?: PhaseWorkflowType | null;
    workflowDescription?: string | null;
    mode?: TasklistMode;
    collapsed?: boolean;
    onToggleCollapsed?: () => void;
    subtaskCount?: number;
};

export function TaskStatusIconView({
    isVisible,
    statusInfo,
    handleClick,
    cursor,
    phaseName,
    dueDateText,
    textOnly = false,
    workflowType,
    workflowDescription,
    mode,
    collapsed = false,
    onToggleCollapsed,
    subtaskCount = 0
}: ITaskStatusIconViewProps) {

    if (!isVisible) return null;

    // Same labels the picker and the template badge use, so a renamed type reads the same everywhere.
    const workflowLabel = workflowType != null ? WORKFLOW_TYPE_LABELS[workflowType] ?? "" : "";
    const inTemplates = mode === "templates";

    // Template editor keeps the visible pill (it's the workflow-config UX).
    // Instance views drop the pill and surface the type via a hover tooltip on the
    // tasklist name to keep the grant tasklist chrome quieter.
    const workflowPill = inTemplates && workflowLabel ? (
        <span data-testid="workflow-type-pill" style={{
            padding: "1px 6px",
            backgroundColor: "#e0e0e0",
            borderRadius: "8px",
            fontSize: "10px",
            fontWeight: 500,
            color: "#555",
            marginLeft: "6px"
        }}>
            {workflowLabel}
        </span>
    ) : null;

    // Phases with no status info (Submission) show just the name.
    const showStatusIcon = !!statusInfo && !textOnly;

    // Hover help on the instance tasklist: the type plus its description, which says how the
    // task status indicator is derived for that type. The template editor shows the same
    // description on its workflow type badge instead.
    const tooltip = !inTemplates && workflowLabel ? (
        <>
            <div style={{ fontWeight: 600 }}>Workflow: {workflowLabel}</div>
            {workflowDescription && (
                <div style={{ fontSize: "12px", lineHeight: 1.4, marginTop: "4px" }}>{workflowDescription}</div>
            )}
        </>
    ) : null;

    // The task expander lives in the status icon rather than beside it (ticket 1034): a minus when
    // open, a plus when closed. Tasks with no status icon (Submission, text-only) keep a chevron,
    // which is the only expander they can have. Either way the click is the toggle's, not the
    // heading's, so opening a task no longer opens its summary as well.
    const subtaskWord = subtaskCount === 1 ? "subtask" : "subtasks";
    const toggleHint = collapsed ? `Show ${subtaskCount} ${subtaskWord}` : `Hide ${subtaskCount} ${subtaskWord}`;
    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggleCollapsed?.();
    };

    const expander = !onToggleCollapsed ? null : showStatusIcon ? (
        <span
            role="button"
            tabIndex={0}
            aria-expanded={!collapsed}
            aria-label={toggleHint}
            title={toggleHint}
            data-testid="task-collapse-toggle"
            style={{ display: "inline-flex", cursor: "pointer" }}
            onClick={handleToggle}
            onKeyDown={(e) => {
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                e.stopPropagation();
                onToggleCollapsed();
            }}
        >
            <StatusIcon info={statusInfo!} diameter={22} expandState={collapsed ? "collapsed" : "expanded"} />
        </span>
    ) : (
        <DxButton
            className="tasklist-collapse-toggle"
            icon={collapsed ? "chevronright" : "chevrondown"}
            stylingMode="text"
            hint={toggleHint}
            elementAttr={{ "data-testid": "task-collapse-toggle", "aria-expanded": !collapsed }}
            onClick={(e) => {
                e.event?.stopPropagation();
                onToggleCollapsed();
            }}
        />
    );

    const leading = expander ?? (showStatusIcon ? <StatusIcon info={statusInfo!} diameter={22} /> : undefined);

    return (
        <PhaseNameHeading
            name={phaseName}
            onClick={handleClick}
            cursor={cursor}
            tooltipText={tooltip}
            suffix={statusInfo ? <span style={{ fontSize: "0.75em" }}>{dueDateText}</span> : undefined}
            leading={leading}
            trailing={workflowPill}
            headingPaddingLeft={showStatusIcon ? "10px" : "0px"}
        />
    );
}
